import { useQuery, UseQueryResult } from "react-query";

import { AssessmentType } from "@constants/assessmentTypes";
import type { Status } from "api/types/aba";
import { getDocumentChecklist } from "api/documentGenerationChecklist";

export interface ChecklistItem {
  /** Internal field key (e.g. "client_first_name") */
  name: string;
  /** Human-readable field label (e.g. "Client First Name") */
  label: string;
  /** Grouping category (e.g. "Client Profile") */
  category: string;
  /** Navigation section name for the "Go To" button (e.g. "Personal Information") */
  section?: string;
  /** Descriptive help text explaining what is needed */
  description: string;
  /** Current field value, null if not present */
  value: unknown;
  /** Whether the required data is present */
  isPresent: boolean;
}

export interface AssessmentStatus {
  /** Assessment type identifier */
  type: AssessmentType;
  /** Id of the underlying row in this type's own assessment table (pair with `type`;
   *  e.g. QABF → qabf_assessment.id). Null when never administered. */
  assessmentId?: number | null;
  /** ISO date string of last administration, or null */
  lastCompletedAt: string | null;
  /** Whether the most recent completion satisfies the required recency window */
  isUpToDate: boolean;
  /** Whether the most recent administration has its scoring grid attached.
   *  Only meaningful for AFLS / SOCIALLY_SAVVY / CPFQ_CHILD / CPFQ_CAREGIVER; null on others. */
  hasMostRecentGrid?: boolean | null;
  /** Behavior reduction goal the assessment is required for. Only set on QABF. */
  relatedGoal?: string | null;
  /** QABF only: `false` = caregiver-subject goal (informational, non-blocking); absent/true behaves as required. */
  isRequired?: boolean;
}

/** Lifecycle status of a goal for the Reassessment Treatment Plan view (BE hyphenated values) */
export type GoalLifecycleStatus = "in-progress" | "new" | "met" | "discontinued" | "on-hold";

/**
 * Subset of fields the FE consumes from a CR target. The BE response carries
 * additional config (sub_config, step35_config, mastery_criteria, etc.) —
 * extend this interface as we start using them.
 */
export interface Target {
  /** Target id */
  id: number | string;
  /** Target display name */
  title: string;
  /** L1 behavioral dimension (e.g. "frequency", "duration", "latency", "magnitude") — null until BCBA configures it */
  l1Dimension: string | null;
  /** L3 instructional context (e.g. "discrete-trial", "free-operant") — null until BCBA configures it */
  l3Context: string | null;
  /** Stored mastery criteria JSON (e.g. { percentThreshold, consecutiveDataPoints }) */
  masteryCriteria?: unknown;
  /** Stored step 3.5 config JSON (e.g. { promptHierarchy, countPromptedAsCorrect }) */
  step35Config?: unknown;
  /** Target lifecycle status (e.g. "in-progress", "met") — CR's closed status enum */
  status?: Status | null;
}

export interface GoalStatus {
  /** Goal id */
  id: number | string;
  /** Goal title (e.g. "Reduce aggression") */
  title: string;
  /** Goal statement / description. May be null. */
  description: string | null;
  /** Goal type (e.g. "skill-acquisition", "behavior-reduction") — whatever CR returns */
  goalType: string;
  /** True if the goal is missing baseline data — surfaces in "Goals not included" with a Go To button */
  isMissingBaselineData: boolean;
  /** Non-archived CR targets. */
  targets: Target[];
  /** Lifecycle status — present only on reassessment_treatment_plan responses. Absent on initial. */
  status?: GoalLifecycleStatus | string | null;
  /** ISO timestamp of the latest status change. Populated only when status is "met", "on-hold", or "discontinued". */
  statusChangedAt?: string | null;
  /** Rationale text captured on the latest status change. Populated only when status is "met", "on-hold", or "discontinued". */
  statusRationale?: string | null;
  /** Replacement-behavior targets pointing at this goal. Present only on behavior-reduction goals. */
  replacementBehaviors?: { targetName: string }[];
  /** True when a behavior-reduction goal has no replacement-behavior target. Present only on behavior-reduction goals. */
  isMissingReplacementBehavior?: boolean;
  /** Raw CR passthrough — full goal object from CR is spread onto the response. Narrow at the consumer for typed access. */
  [key: string]: unknown;
}

export interface PayorRequiredAssessmentsRule {
  operator: "AND" | "OR";
  assessmentTypes: AssessmentType[];
}

export interface PayorRules {
  requiredAssessments: PayorRequiredAssessmentsRule | null;
  requiresReferringProvider: boolean;
  requiresCriminalLegalHistory: boolean;
  requiresDrugAlcoholUse: boolean;
  requiresTraumaHistory: boolean;
}

interface UseGetDocumentChecklistReturn {
  checklist: ChecklistItem[];
  assessments: AssessmentStatus[];
  goals: GoalStatus[];
  payorRules: PayorRules | null;
  checklistLoading: boolean;
  checklistFetching: boolean;
  refetchChecklist: () => void;
}

/**
 * Fetches the document generation checklist (field-level items + assessments) for a given
 * client and document type.
 * @param clientId - The client ID.
 * @param documentType - The document type key (e.g. "initial_treatment_plan").
 * @param clinicianId - Optional. When provided, enables the IR clinician credentials check.
 */
const useGetDocumentChecklist = (
  clientId?: string | number,
  documentType?: string,
  clinicianId?: string | number
): UseGetDocumentChecklistReturn &
  Omit<UseQueryResult, "data" | "isLoading" | "isFetching" | "refetch"> => {
  const { data, isLoading, isFetching, refetch, ...rest } = useQuery(
    ["documentChecklist", documentType, clientId, clinicianId],
    () => getDocumentChecklist(clientId!, documentType!, clinicianId),
    {
      enabled: Boolean(clientId) && Boolean(documentType),
      // staleTime 0 guarantees a fresh fetch on entry (fixes the frozen "0 of 0" snapshot),
      // but disable focus/reconnect refetches so it only refires on entry + manual Check Again.
      staleTime: 0,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    }
  );

  return {
    checklist: data?.data?.checklist || [],
    assessments: data?.data?.assessments || [],
    goals: data?.data?.goals || [],
    payorRules: data?.data?.payorRules ?? null,
    checklistLoading: isLoading,
    checklistFetching: isFetching,
    refetchChecklist: refetch,
    ...rest,
  };
};

export default useGetDocumentChecklist;
